import type { SaveEyeExaminationInput } from '@optiflow/types';
import { apiErrorCode, apiErrorDetails, isNetworkError } from '@/services/apiClient';
import { formFieldForServerField, type FieldError, type FormErrors } from './examinationForm';

export type WriteOutcome =
  { kind: 'conflict' } | { kind: 'refused'; banner: FieldError; fields: FormErrors };

/** Server details that deserve their own words rather than a general "check this value". */
const FIELD_MESSAGES: Record<string, string> = {
  'prescription.expiresOn': 'medical.errors.expiryBeforeIssue',
  'prescription.pdMeasurementId': 'medical.errors.pdUnavailable',
  examinerId: 'medical.review.examinerRequired',
};

/**
 * What a refused save or completion means for the screen, from the error code and the fields it names.
 *
 * The server's own sentence is never shown: the screen speaks the user's language, and a raw database message has no place on it. A stale version is a conflict the user resolves by reloading, never something retried over.
 */
export function readWriteError(error: unknown, payload: SaveEyeExaminationInput): WriteOutcome {
  const code = apiErrorCode(error);

  if (code === 'CONFLICT') return { kind: 'conflict' };

  if (code === 'VALIDATION_FAILED') {
    const fields: FormErrors = {};
    let banner: FieldError = { key: 'medical.errors.validation' };

    for (const detail of apiErrorDetails(error)) {
      if (!detail.field) continue;
      if (detail.field === 'customerId') banner = { key: 'medical.errors.inactiveCustomer' };
      if (detail.field === 'prescription') banner = { key: 'medical.errors.prescriptionEmpty' };

      const formField = formFieldForServerField(detail.field, payload);
      if (formField) {
        fields[formField] = { key: FIELD_MESSAGES[detail.field] ?? 'medical.errors.server' };
      }
    }
    return { kind: 'refused', banner, fields };
  }

  return { kind: 'refused', banner: refusalBanner(error), fields: {} };
}

const BANNERS: Record<string, string> = {
  PERMISSION_DENIED: 'medical.errors.permission',
  FORBIDDEN: 'medical.errors.notExaminer',
  BRANCH_ACCESS_DENIED: 'medical.errors.branch',
  BRANCH_CONTEXT_MISSING: 'medical.page.noBranch',
  NOT_FOUND: 'medical.errors.notFound',
  REQUEST_IN_PROGRESS: 'medical.errors.retry',
  IDEMPOTENCY_KEY_REUSED: 'medical.errors.retry',
};

/** A refusal that is not about a field, in the user's words. */
function refusalBanner(error: unknown): FieldError {
  const code = apiErrorCode(error);
  const key =
    (code ? BANNERS[code] : undefined) ??
    (isNetworkError(error) ? 'medical.errors.network' : 'medical.errors.unexpected');
  return { key };
}

export type LifecycleAction = 'amend' | 'void' | 'discard';

/**
 * Why an amendment, a void or a discard was refused, other than a conflict — which the page resolves by reading the record again, because the same 409 means different things depending on what changed.
 */
export function readLifecycleRefusal(action: LifecycleAction, error: unknown): FieldError {
  if (apiErrorCode(error) !== 'VALIDATION_FAILED') return refusalBanner(error);
  const fields = apiErrorDetails(error).map((detail) => detail.field);
  if (action === 'amend' && fields.includes('customerId')) {
    return { key: 'medical.errors.inactiveCustomerAmendment' };
  }
  if (fields.includes('reason')) {
    return { key: 'medical.lifecycle.reasonRequired', values: { min: '3' } };
  }
  return { key: 'medical.errors.validation' };
}

/** Why an examination could not be loaded, in the user's words. */
export function loadErrorKey(error: unknown): string {
  const code = apiErrorCode(error);
  if (code === 'NOT_FOUND') return 'medical.errors.notFound';
  if (code === 'PERMISSION_DENIED' || code === 'FORBIDDEN') return 'medical.errors.permission';
  if (isNetworkError(error)) return 'medical.errors.network';
  return 'medical.page.loadError';
}
